<script setup>
import { useDialogPluginComponent } from 'quasar';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import HrBasicDialog from '../HrBasicDialog.vue';
import HrBtn from '../HrBtn.vue';

/**
 * Standard confirmation dialog presented via confirmDialog() with cancel and confirm buttons.
 *
 * @status internal
 * @group cards-dialogs
 */
defineOptions({ name: 'HrConfirmDialog' });

const props = defineProps({
  /** Dialog headline. */
  title: {
    type: String,
    required: true,
  },
  /** Supporting text describing the consequence of the action. */
  message: {
    type: String,
    required: true,
  },
  /** Label for the confirm action button. Defaults to the common.buttonConfirm translation. */
  confirmLabel: {
    type: String,
    default: null,
  },
  /** Label for the cancel action button. Defaults to the common.buttonCancel translation. */
  cancelLabel: {
    type: String,
    default: null,
  },
  /**
   * Prevents dismissal via backdrop click or Escape key.
   * Defaults to true — a confirm dialog requires an explicit choice.
   */
  persistent: {
    type: Boolean,
    default: true,
  },
});

defineEmits([...useDialogPluginComponent.emits]);

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent();

const { t } = useI18n();
const effectiveConfirmLabel = computed(() => props.confirmLabel ?? t('common.buttonConfirm'));
const effectiveCancelLabel = computed(() => props.cancelLabel ?? t('common.buttonCancel'));
</script>

<template>
  <hr-basic-dialog
    ref="dialogRef"
    :persistent="persistent"
    @hide="onDialogHide"
  >
    <div class="hr-dialog">
      <p class="hr-dialog__headline hr-headline-small">
        {{ title }}
      </p>
      <p class="hr-dialog__supporting-text hr-body-medium">
        {{ message }}
      </p>
      <div class="hr-dialog__actions">
        <!-- @todo When the legacy theme is removed: remove :legacy-props -->
        <hr-btn
          variant="text"
          :label="effectiveCancelLabel"
          :legacy-props="{ flat: true, color: 'secondary' }"
          data-e2e="button-cancel"
          @click="() => onDialogCancel()"
        />
        <hr-btn
          variant="text"
          :label="effectiveConfirmLabel"
          :legacy-props="{ flat: true, color: 'primary' }"
          data-autofocus
          data-e2e="button-confirm"
          @click="() => onDialogOK()"
        />
      </div>
    </div>
  </hr-basic-dialog>
</template>

<style lang="scss" scoped>
.hr-dialog {
  background: var(--app-surface-container-high);
  border-radius: var(--app-shape-corner-extra-large);
  padding: 24px;
  display: flex;
  flex-direction: column;
}

.hr-dialog__headline {
  color: var(--app-on-surface);
  margin: 0 0 16px;
}

.hr-dialog__supporting-text {
  color: var(--app-on-surface-variant);
  margin: 0;
  /* Supporting text is plain text; honor authored line breaks (\n) without allowing HTML. */
  white-space: pre-line;
}

.hr-dialog__actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}
</style>
